import { getKeyframeConfigs } from "./AnimationKeyframeHandler";
import {
  AnimationConfig,
  AnimationConfigExtraParams,
  AnimationConfigParams,
  BLOCK_REVEAL_ANIMATIONS,
  CUSTOM_ANIMATIONS,
  FADE_ANIMATIONS,
  FLICKER_ANIMATIONS,
  FLY_ANIMATIONS,
  KeyframeConfig,
  PULSE_ANIMATIONS,
  REVEAL_ANIMATIONS,
  SHIFT_ANIMATIONS,
  TUMBLE_ANIMATIONS,
  WIPE_ANIMATIONS,
  ZOOM_ANIMATIONS,
} from "../../models/animations.model";
import {
  moveToRelativeLayerAsObject,
  setClipPath,
  setDefaultClipPath,
} from "../../utils/ObjectUtil";
import { AnimeParams } from "animejs";
import { fabric } from "fabric";
import { getNumberOfLoops } from "../../utils/animation.util";

export const storeOriginalProperties = (
  animatedObjects: any[],
  fabricObject: any
) => {
  animatedObjects[fabricObject.id] = {
    ...animatedObjects[fabricObject.id],
    top: fabricObject.top,
    left: fabricObject.left,
    width: fabricObject.width,
    height: fabricObject.height,
    opacity: fabricObject.opacity,
    angle: fabricObject.angle,
    scaleX: fabricObject.scaleX,
    scaleY: fabricObject.scaleY,
    originX: fabricObject.originX,
    originY: fabricObject.originY,
    clipPath: undefined,
  };

  if (fabricObject.clipPath) {
    animatedObjects[fabricObject.id].clipPath = fabric.util.object.clone(
      fabricObject.clipPath
    );
  }
};

export const getAnimationConfig = (
  params: AnimationConfigParams
): AnimationConfig[] => {
  const { fabricObjectClone, animatedObjects } = params;

  if (params.storeOriginalProperties) {
    storeOriginalProperties(animatedObjects, fabricObjectClone);
  }

  /**
   * Temporary fix for first frame issue
   */
  doFirstFrameFix(params);

  /**
   * Setup extra requirements
   * Such as adding black strip for block reveal
   */
  doExtraSetup(params);

  return getAnimationParams(params);
};

export const getDirtyUpdateExtraParams = (fabricObject: any): AnimeParams => {
  return {
    update: () => {
      fabricObject.set("dirty", true);
      fabricObject.clipPath?.set("dirty", true);
    },
  };
};

export const getAddWithUpdateExtraParams = (fabricObject: any): AnimeParams => {
  return {
    update: () => {
      fabricObject.addWithUpdate();
    },
  };
};

export const getExtraParams = (
  fabricObject: any,
  animationType:
    | WIPE_ANIMATIONS
    | REVEAL_ANIMATIONS
    | BLOCK_REVEAL_ANIMATIONS
    | CUSTOM_ANIMATIONS,
  keyframeConfig: KeyframeConfig,
  extras: AnimationConfigExtraParams
): AnimeParams | null => {
  switch (animationType) {
    case WIPE_ANIMATIONS.WIPE_IN_LEFT:
    case WIPE_ANIMATIONS.WIPE_IN_BOTTOM:
    case WIPE_ANIMATIONS.WIPE_IN_TOP:
    case WIPE_ANIMATIONS.WIPE_IN_RIGHT:
    case WIPE_ANIMATIONS.WIPE_OUT_BOTTOM:
    case WIPE_ANIMATIONS.WIPE_OUT_RIGHT:
    case WIPE_ANIMATIONS.WIPE_OUT_TOP:
    case WIPE_ANIMATIONS.WIPE_OUT_LEFT:
    case REVEAL_ANIMATIONS.REVEAL_IN_BOTTOM:
    case REVEAL_ANIMATIONS.REVEAL_IN_RIGHT:
    case REVEAL_ANIMATIONS.REVEAL_IN_TOP:
    case REVEAL_ANIMATIONS.REVEAL_IN_LEFT:
    case REVEAL_ANIMATIONS.REVEAL_OUT_BOTTOM:
    case REVEAL_ANIMATIONS.REVEAL_OUT_RIGHT:
    case REVEAL_ANIMATIONS.REVEAL_OUT_TOP:
    case REVEAL_ANIMATIONS.REVEAL_OUT_LEFT:
      if (keyframeConfig.option.targets !== fabricObject.clipPath) {
        return {};
      }

      return getDirtyUpdateExtraParams(fabricObject);
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_LEFT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_LEFT:
      const objects = extras.objects;

      if (!objects) {
        console.error("No objects found in extra params");
        return {};
      }

      if (
        keyframeConfig.option.targets === objects[0] ||
        keyframeConfig.option.targets === objects[0].clipPath
      ) {
        return getDirtyUpdateExtraParams(objects[0]);
      }

      return {};
    case CUSTOM_ANIMATIONS.GREAT_THINKER:
    case CUSTOM_ANIMATIONS.BEAUTIFUL_QUESTIONS:
    case CUSTOM_ANIMATIONS.RISING_STRONG_IN:
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
    case CUSTOM_ANIMATIONS.MADE_WITH_LOVE:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_IN:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
    case CUSTOM_ANIMATIONS.FIND_YOUR_ELEMENT:
    case CUSTOM_ANIMATIONS.THURSDAY:
    case CUSTOM_ANIMATIONS.HELLO_GOODBYE:
      return getDirtyUpdateExtraParams(fabricObject);
    case CUSTOM_ANIMATIONS.SUNNY_MORNINGS:
    case CUSTOM_ANIMATIONS.DOMINO_DREAMS:
    case CUSTOM_ANIMATIONS.COFFEE_MORNINGS:
      return getAddWithUpdateExtraParams(fabricObject);
    default:
      return {};
  }
};

export const getAnimationParams = (
  params: AnimationConfigParams
): AnimationConfig[] => {
  const { fabricObject, effect } = params;
  const { direction, easing, type } = effect;
  let allConfigs: AnimationConfig[] = [];

  const keyframeConfigs = getKeyframeConfigs(params);

  for (const keyframeConfig of keyframeConfigs) {
    const extraParams = getExtraParams(
      fabricObject,
      type,
      keyframeConfig,
      params.extras
    );

    const animationConfig: AnimationConfig = {
      option: {
        direction,
        easing,
        ...keyframeConfig.option,
        targets: keyframeConfig.option.targets ?? fabricObject,
        ...extraParams,
      },
      offset: keyframeConfig.offset ?? 0,
    };

    allConfigs.push(animationConfig);
  }

  /**
   * Do Loop config
   */
  allConfigs = doLoopedConfigs(params, allConfigs);

  const completeAction = getCompleteAction(params);
  if (completeAction) {
    allConfigs[allConfigs.length - 1].option.complete = completeAction;
  }

  return allConfigs;
};

export const doExtraSetup = (params: AnimationConfigParams) => {
  switch (params.effect.type) {
    case WIPE_ANIMATIONS.WIPE_IN_LEFT:
    case WIPE_ANIMATIONS.WIPE_IN_BOTTOM:
    case WIPE_ANIMATIONS.WIPE_IN_RIGHT:
    case WIPE_ANIMATIONS.WIPE_IN_TOP:
    case WIPE_ANIMATIONS.WIPE_OUT_BOTTOM:
    case WIPE_ANIMATIONS.WIPE_OUT_RIGHT:
    case WIPE_ANIMATIONS.WIPE_OUT_TOP:
    case WIPE_ANIMATIONS.WIPE_OUT_LEFT:
    case REVEAL_ANIMATIONS.REVEAL_IN_BOTTOM:
    case REVEAL_ANIMATIONS.REVEAL_IN_RIGHT:
    case REVEAL_ANIMATIONS.REVEAL_IN_TOP:
    case REVEAL_ANIMATIONS.REVEAL_IN_LEFT:
    case REVEAL_ANIMATIONS.REVEAL_OUT_BOTTOM:
    case REVEAL_ANIMATIONS.REVEAL_OUT_RIGHT:
    case REVEAL_ANIMATIONS.REVEAL_OUT_TOP:
    case REVEAL_ANIMATIONS.REVEAL_OUT_LEFT:
      if (!params.fabricObject.clipPath) {
        setDefaultClipPath(params.fabricObject);
      }

      if (!params.fabricObjectClone.clipPath) {
        setDefaultClipPath(params.fabricObjectClone);
      }

      break;
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_LEFT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_LEFT:
      if (!params.extras.canvas || !params.extras.objects) {
        console.error(
          `Original object or canvas not provided for ${params.effect.type} at doExtraSetup`
        );
        break;
      }

      /**
       * Add reveal strip to canvas
       */
      addToCanvas(params.extras.canvas, params.extras.objects[0]);

      /**
       * Move to correct layer
       */
      moveToRelativeLayerAsObject(
        params.extras.canvas,
        params.fabricObject,
        params.extras.objects[0],
        2
      );

      break;
    case CUSTOM_ANIMATIONS.GREAT_THINKER:
    case CUSTOM_ANIMATIONS.SUNNY_MORNINGS:
    case CUSTOM_ANIMATIONS.RISING_STRONG_IN:
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
    case CUSTOM_ANIMATIONS.MADE_WITH_LOVE:
    case CUSTOM_ANIMATIONS.DOMINO_DREAMS:
    case CUSTOM_ANIMATIONS.COFFEE_MORNINGS:
      doCharacterLevelAnimationsSetup(params);
      break;
    case CUSTOM_ANIMATIONS.BEAUTIFUL_QUESTIONS:
      if (!params.extras.objects) {
        console.error(
          `Original object not provided for ${params.effect.type} at doExtraSetup`
        );
        return;
      }

      /**
       * Set clip path
       */
      setClipPath(
        params.fabricObject,
        new fabric.Rect({
          width: params.extras.objects[0].width,
          height: params.extras.objects[0].height * 3,
          top: -params.extras.objects[0].height,
          left: 0,
          originX: "center",
          originY: "center",
        })
      );

      doCharacterLevelAnimationsSetup(params);

      break;
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_IN:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
      if (!params.fabricObject.clipPath) {
        setDefaultClipPath(params.fabricObject);
      }

      doCharacterLevelAnimationsSetup(params);

      break;
    case CUSTOM_ANIMATIONS.FIND_YOUR_ELEMENT:
      if (!params.extras.objects || !params.extras.canvas) {
        console.error(
          `Original object or canvas not provided for ${params.effect.type} at doExtraSetup`
        );
        return;
      }

      if (!params.fabricObject.clipPath) {
        setDefaultClipPath(params.fabricObject);
      }

      /**
       * Add bottom line to canvas
       */
      addToCanvas(params.extras.canvas, params.extras.objects[1]);

      /**
       * Move to correct layer
       */
      moveToRelativeLayerAsObject(
        params.extras.canvas,
        params.extras.objects[0],
        params.extras.objects[1],
        2
      );

      doCharacterLevelAnimationsSetup(params);

      break;
    case CUSTOM_ANIMATIONS.THURSDAY:
      if (!params.extras.objects || !params.extras.canvas) {
        console.error(
          `Original object or canvas not provided for ${params.effect.type} at doExtraSetup`
        );
        return;
      }

      if (!params.fabricObject.clipPath) {
        setDefaultClipPath(params.fabricObject);
      }

      /**
       * Add top and bottom line to canvas
       */
      addToCanvas(params.extras.canvas, params.extras.objects[1]);
      addToCanvas(params.extras.canvas, params.extras.objects[2]);

      /**
       * Move to correct layer
       */
      moveToRelativeLayerAsObject(
        params.extras.canvas,
        params.extras.objects[0],
        params.extras.objects[1],
        2
      );
      moveToRelativeLayerAsObject(
        params.extras.canvas,
        params.extras.objects[0],
        params.extras.objects[2],
        2
      );

      doCharacterLevelAnimationsSetup(params);

      break;
    case CUSTOM_ANIMATIONS.HELLO_GOODBYE:
      if (!params.extras.objects || !params.extras.canvas) {
        console.error(
          `Original object or canvas not provided for ${params.effect.type} at doExtraSetup`
        );
        return;
      }

      /**
       * Add left line to canvas
       */
      addToCanvas(params.extras.canvas, params.extras.objects[1]);

      /**
       * Move to correct layer
       */
      moveToRelativeLayerAsObject(
        params.extras.canvas,
        params.extras.objects[0],
        params.extras.objects[1],
        2
      );

      doCharacterLevelAnimationsSetup(params);
      break;
    default:
      return;
  }
};

export const addToCanvas = (canvas: fabric.Canvas, object: any) => {
  if (!canvas || !object) {
    return;
  }

  canvas.add(object);
};

export const getLoopedConfigs = (
  configs: AnimationConfig[],
  loopCount: number,
  duration: number
): AnimationConfig[] => {
  const loopConfigs: AnimationConfig[] = [...configs];

  for (let i = 0; i < loopCount; i++) {
    for (const config of configs) {
      const newConfig: AnimationConfig = {
        option: config.option,
        offset: duration * (i + 1),
      };
      loopConfigs.push(newConfig);
    }
  }

  /**
   * Remove complete action from the last loop
   */
  delete loopConfigs[loopConfigs.length - 1].option.complete;

  return loopConfigs;
};

export const getCompleteAction = (params: AnimationConfigParams) => {
  switch (params.effect.type) {
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_LEFT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_LEFT:
      return () => {
        if (!params.extras.canvas || !params.extras.objects) {
          console.error(
            "Either canvas or blockRect not provided for complete action"
          );
          return null;
        }

        params.extras.canvas.remove(params.extras.objects[0]);
      };
    case CUSTOM_ANIMATIONS.GREAT_THINKER:
    case CUSTOM_ANIMATIONS.SUNNY_MORNINGS:
    case CUSTOM_ANIMATIONS.RISING_STRONG_IN:
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
    case CUSTOM_ANIMATIONS.MADE_WITH_LOVE:
    case CUSTOM_ANIMATIONS.BEAUTIFUL_QUESTIONS:
    case CUSTOM_ANIMATIONS.DOMINO_DREAMS:
    case CUSTOM_ANIMATIONS.COFFEE_MORNINGS:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_IN:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
    case CUSTOM_ANIMATIONS.FIND_YOUR_ELEMENT:
    case CUSTOM_ANIMATIONS.THURSDAY:
    case CUSTOM_ANIMATIONS.HELLO_GOODBYE:
      return () => {
        if (!params.isSinglePreview) {
          return;
        }

        if (!params.extras.canvas || !params.extras.objects) {
          console.error(
            "Either canvas or blockRect not provided for complete action"
          );
          return;
        }

        /**
         * Remove dummy text group from canvas
         */
        params.extras.canvas.remove(params.fabricObject);

        /**
         * Restore original object opacity
         */
        params.extras.objects[0].opacity = params.fabricObjectClone.opacity;
      };
    default:
      return null;
  }
};

export const doLoopedConfigs = (
  params: AnimationConfigParams,
  allConfigs: AnimationConfig[]
) => {
  const { effect, loopEnabled, loopOption } = params;

  if (!loopEnabled) {
    return allConfigs;
  }

  const loopCount = getNumberOfLoops(
    loopOption.trans_duration - effect.trans_duration,
    effect.trans_duration
  );

  if (loopCount === 0) {
    return allConfigs;
  }

  let loopedConfigs = getLoopedConfigs(
    allConfigs,
    loopCount,
    effect.trans_duration
  );

  return loopedConfigs;
};

export const doCharacterLevelAnimationsSetup = (
  params: AnimationConfigParams
) => {
  if (!params.extras.objects) {
    console.error(
      `Original object not provided for ${params.effect.type} at doExtraSetup`
    );
    return;
  }

  /**
   * Set Delay
   */
  params.extras.delay =
    params.effect.trans_duration /
    (2 * (params.fabricObject.getObjects().length - 1));
};

export const doFirstFrameFix = (params: AnimationConfigParams) => {
  switch (params.effect.type) {
    case FADE_ANIMATIONS.FADE_IN:
    case FLY_ANIMATIONS.FLY_IN_BOTTOM:
    case FLY_ANIMATIONS.FLY_IN_TOP:
    case FLY_ANIMATIONS.FLY_IN_LEFT:
    case FLY_ANIMATIONS.FLY_IN_RIGHT:
    case TUMBLE_ANIMATIONS.TUMBLE_IN:
    case ZOOM_ANIMATIONS.BREATHE:
    case ZOOM_ANIMATIONS.STOMP:
    case ZOOM_ANIMATIONS.ZOOM_IN:
    case SHIFT_ANIMATIONS.SHIFT_TOP:
    case SHIFT_ANIMATIONS.SHIFT_BOTTOM:
    case SHIFT_ANIMATIONS.SHIFT_LEFT:
    case SHIFT_ANIMATIONS.SHIFT_RIGHT:
    case PULSE_ANIMATIONS.PULSE:
    case FLICKER_ANIMATIONS.FLICKER:
    case WIPE_ANIMATIONS.WIPE_IN_LEFT:
    case WIPE_ANIMATIONS.WIPE_IN_BOTTOM:
    case WIPE_ANIMATIONS.WIPE_IN_RIGHT:
    case WIPE_ANIMATIONS.WIPE_IN_TOP:
    case REVEAL_ANIMATIONS.REVEAL_IN_BOTTOM:
    case REVEAL_ANIMATIONS.REVEAL_IN_RIGHT:
    case REVEAL_ANIMATIONS.REVEAL_IN_TOP:
    case REVEAL_ANIMATIONS.REVEAL_IN_LEFT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_LEFT:
      params.fabricObject.set("opacity", 0);
      break;
    default:
      return;
  }
};
